import products from "generation/products.json";

// Links in CMS content that wrap only an image have no text a screen reader
// can announce, and core Image always renders alt="", so the CMS alt text is
// lost. These helpers give such links an aria-label before they are rendered.

const nodeText = (node) =>
  node.type === "text"
    ? node.data
    : (node.children || []).map(nodeText).join(" ");

const collapse = (text) => text.replace(/\s+/g, " ").trim();

const findNode = (node, test) => {
  if (test(node)) return node;
  for (const child of node.children || []) {
    const found = findNode(child, test);
    if (found) return found;
  }
  return null;
};

const hasAccessibleName = (node) =>
  collapse(nodeText(node)) !== "" ||
  !!node.attribs["aria-label"]?.trim() ||
  !!node.attribs["aria-labelledby"]?.trim() ||
  !!node.attribs.title?.trim();

const imageAltLabel = (node) => {
  const image = findNode(
    node,
    (child) => child.name === "img" && !!child.attribs?.alt?.trim()
  );
  return image ? collapse(image.attribs.alt) : "";
};

const siblingLinkLabel = (node) => {
  const href = node.attribs.href;
  const figure = node.parent;
  if (!href || !figure || figure.name !== "figure") return "";
  for (let sibling = figure.next; sibling; sibling = sibling.next) {
    const link = findNode(
      sibling,
      (child) =>
        child.name === "a" && child !== node && child.attribs?.href === href
    );
    if (link) {
      const label = findNode(link, (child) =>
        (child.attribs?.class || "").split(" ").includes("item-label")
      );
      return collapse(nodeText(label || link));
    }
  }
  return "";
};

const captionLabel = (node) => {
  const wrapper = node.parent;
  if (!wrapper || wrapper.name !== "p" || collapse(nodeText(wrapper)) !== "") {
    return "";
  }
  let caption = wrapper.next;
  while (caption && caption.type === "text" && !caption.data.trim()) {
    caption = caption.next;
  }
  return caption && caption.name === "p" ? collapse(nodeText(caption)) : "";
};

let productNames;
const productLinkLabel = (node) => {
  const href = node.attribs.href;
  if (!href || href.includes("http")) return "";
  if (!productNames) {
    productNames = {};
    Object.values(products.default || products).forEach((product) => {
      if (product?.url_key) productNames[product.url_key] = product.name;
    });
  }
  return productNames[href.replace(/^\/+|\/+$/g, "")] || "";
};

const homeLinkLabel = (node) => {
  const href = node.attribs.href;
  if (!href) return "";
  const match = href.match(/^(?:https?:\/\/([^/]+))?\/?$/);
  if (!match || (match[1] && !match[1].includes("primalighting"))) return "";
  return "Prima Lighting home";
};

const nameImageLink = (node) => {
  if (node.type !== "tag" || node.name !== "a" || hasAccessibleName(node)) {
    return;
  }
  const label =
    imageAltLabel(node) ||
    siblingLinkLabel(node) ||
    captionLabel(node) ||
    productLinkLabel(node) ||
    homeLinkLabel(node);
  if (label) node.attribs["aria-label"] = label;
};

export const nameImageLinks = (nodes) => {
  nodes.forEach((node) => {
    nameImageLink(node);
    if (node.children) nameImageLinks(node.children);
  });
  return nodes;
};
